import * as RadixDialog from "@radix-ui/react-dialog";
import { useEffect, useId, useMemo, useState, type FormEvent, type ReactNode } from "react";
import styled from "@emotion/styled";
import { kfModalContentIn, kfOverlayIn, twRing, twTransform } from "@/lib/emotionTw";
import { Button } from "@/components/primitives/Button";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import type { TableColumnVisibilityColumn, ViewFilter, ViewType, SaveViewPayload, SaveViewResult } from "./DataTable.types";

/* Exact compiled output of the pre-migration Tailwind utilities. `&&`
 * overrides on Button reproduce the old tailwind-merge results. */

const SaveViewOverlay = styled(RadixDialog.Overlay, {
  label: "AgenticTableSaveView-overlay"
})({
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 50,
  backgroundColor: "hsl(var(--bfrs-color-text-primary) / .45)",
  ...kfOverlayIn,
  animation: "bfrs-bfrs-overlay-in var(--bfrs-motion-fast) var(--bfrs-ease-out)"
});

const SaveViewContent = styled(RadixDialog.Content, {
  label: "AgenticTableSaveView-content"
})({
  position: "fixed",
  left: "50%",
  top: "50%",
  zIndex: 50,
  maxHeight: "calc(100dvh - 2rem)",
  width: "calc(100% - 2rem)",
  maxWidth: "420px",
  ...twTransform({ "--tw-translate-x": "-50%", "--tw-translate-y": "-50%" }),
  overflow: "auto",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  padding: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  "--tw-shadow": "var(--bfrs-shadow-overlay)",
  "--tw-shadow-colored": "var(--bfrs-shadow-overlay)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  ...kfModalContentIn,
  animation: "bfrs-bfrs-modal-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)",
  "@media (min-width:640px)": {
    padding: "1.25rem"
  }
});

const SaveViewForm = styled("form", {
  label: "AgenticTableSaveView-form"
})({
  display: "flex",
  flexDirection: "column"
});

const SaveViewTitle = styled(RadixDialog.Title, {
  label: "AgenticTableSaveView-title"
})({
  fontSize: "1.125rem",
  fontWeight: 700,
  lineHeight: "1.5rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const SaveViewDescription = styled(RadixDialog.Description, {
  label: "AgenticTableSaveView-description"
})({
  marginTop: ".25rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SaveViewField = styled("div", {
  label: "AgenticTableSaveView-field"
})({
  marginTop: "1.25rem"
});

const SaveViewLabel = styled("label", {
  label: "AgenticTableSaveView-label"
})({
  display: "block",
  fontSize: ".75rem",
  fontWeight: 700,
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const SaveViewInput = styled("input", {
  label: "AgenticTableSaveView-input"
})({
  marginTop: ".375rem",
  height: "2.5rem",
  width: "100%",
  borderRadius: "var(--bfrs-radius-md)",
  borderWidth: "1.5px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  fontSize: ".75rem",
  fontWeight: 500,
  lineHeight: 1,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&::placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:focus": {
    ...twRing("2px", "hsl(var(--bfrs-color-primary-soft) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  },
  "&:focus-visible": {
    ...twRing("2px", "hsl(var(--bfrs-color-primary-soft) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  },
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  }
});

const SaveViewSection = styled("section", {
  label: "AgenticTableSaveView-section"
})({
  marginTop: "1.25rem"
});

const SaveViewSectionTitle = styled("div", {
  label: "AgenticTableSaveView-sectionTitle"
})({
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: "1rem",
  letterSpacing: ".12em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SaveViewChips = styled("div", {
  label: "AgenticTableSaveView-chips"
})({
  marginTop: ".5rem",
  display: "flex",
  flexWrap: "wrap",
  gap: ".5rem"
});

const SaveViewChip = styled("span", {
  label: "AgenticTableSaveView-chip",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { muted?: boolean } }>(({ ownerState }) => [
  {
    display: "inline-flex",
    minHeight: "1.75rem",
    alignItems: "center",
    borderRadius: "var(--bfrs-radius-md)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))",
    paddingLeft: ".625rem",
    paddingRight: ".625rem",
    paddingTop: ".25rem",
    paddingBottom: ".25rem",
    fontSize: ".75rem",
    fontWeight: 700,
    lineHeight: 1
  },
  ownerState.muted
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
      }
]);

const SaveViewFooter = styled("div", {
  label: "AgenticTableSaveView-footer"
})({
  marginTop: "1.5rem",
  display: "grid",
  gridTemplateColumns: "repeat(1,minmax(0,1fr))",
  gap: ".625rem",
  "@media (min-width:640px)": {
    gridTemplateColumns: "repeat(2,minmax(0,1fr))"
  }
});

const SaveViewCancelButton = styled(Button, {
  label: "AgenticTableSaveView-cancelButton"
})({
  "&&": {
    height: "2.5rem",
    borderRadius: "var(--bfrs-radius-md)",
    borderWidth: "1.5px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    fontSize: ".75rem",
    lineHeight: "1rem",
    fontWeight: 700,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
    }
  }
});

const SaveViewSubmitButton = styled(Button, {
  label: "AgenticTableSaveView-submitButton"
})({
  "&&": {
    height: "2.5rem",
    borderRadius: "var(--bfrs-radius-md)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-text-muted) / var(--tw-bg-opacity, 1))",
    fontSize: ".75rem",
    lineHeight: "1rem",
    fontWeight: 700,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-inverse) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-text-secondary) / var(--tw-bg-opacity, 1))"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-text-primary) / var(--tw-bg-opacity, 1))"
    }
  }
});

export type TableSaveViewProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  trigger?: ReactNode;
  triggerLabel?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  viewName?: string;
  defaultViewName?: string;
  onViewNameChange?: (viewName: string) => void;
  viewNameLabel?: ReactNode;
  viewNamePlaceholder?: string;
  appliedLabel?: ReactNode;
  appliedSettings?: ReactNode;
  appliedSettingLabels?: string[];
  columns?: TableColumnVisibilityColumn[];
  visibleColumnIds?: string[];
  hiddenColumnCount?: number;
  filtersCount?: number;
  /** The listing type — required to build the API payload. */
  viewType?: ViewType;
  /** Active filters to persist with the view, in API format. */
  filters?: ViewFilter[];
  /** View priority. Higher wins on resolution. Defaults to 0. */
  priority?: number;
  cancelLabel?: ReactNode;
  saveLabel?: ReactNode;
  onCancel?: () => void;
  /**
   * Called on submit with the API-formatted payload.
   * Return a `SaveViewResult` (e.g. from `saveView()`) to trigger `onSaved`
   * and allow the component to detect duplicates.
   */
  onSave?: (payload: SaveViewPayload) => void | Promise<SaveViewResult | void>;
  /** Fires after a successful save with the resolved view and duplicate flag. */
  onSaved?: (result: SaveViewResult) => void;
  loading?: boolean;
  disabled?: boolean;
  closeOnSave?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  preventClose?: boolean;
  className?: string;
  triggerClassName?: string;
};

const isPromiseLike = (value: unknown): value is Promise<unknown> =>
  Boolean(value) && typeof (value as { then?: unknown }).then === "function";

const countHiddenColumns = (columns?: TableColumnVisibilityColumn[], visibleColumnIds?: string[]) => {
  if (!columns || !visibleColumnIds) return 0;

  const visibleIds = new Set(visibleColumnIds);
  return columns.filter((column) => !visibleIds.has(column.id)).length;
};

export function TableSaveView({
  open,
  defaultOpen,
  onOpenChange,
  onClose,
  trigger,
  triggerLabel = "Save",
  title = "Save this view",
  description = "Give your custom view a name to save it for later",
  viewName,
  defaultViewName = "",
  onViewNameChange,
  viewNameLabel = "View Name",
  viewNamePlaceholder = "e.g. High Priority Orders",
  appliedLabel = "APPLIED FILTERS & SETTINGS",
  appliedSettings,
  appliedSettingLabels,
  columns,
  visibleColumnIds = [],
  hiddenColumnCount,
  filtersCount = 0,
  viewType,
  filters,
  priority = 0,
  cancelLabel = "Cancel",
  saveLabel = "Save view",
  onCancel,
  onSave,
  onSaved,
  loading,
  disabled,
  closeOnSave = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  preventClose = false,
  className,
  triggerClassName
}: TableSaveViewProps) {
  const inputId = useId();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false);
  const [internalViewName, setInternalViewName] = useState(defaultViewName);
  const [internalLoading, setInternalLoading] = useState(false);

  const activeOpen = open ?? uncontrolledOpen;
  const activeViewName = viewName ?? internalViewName;
  const activeLoading = Boolean(loading || internalLoading);
  const activeHiddenColumnCount = hiddenColumnCount ?? countHiddenColumns(columns, visibleColumnIds);
  const submitDisabled = disabled || activeLoading || activeViewName.trim().length === 0;

  const fallbackSettingLabels = useMemo(() => {
    const labels: string[] = [];
    if (activeHiddenColumnCount > 0) labels.push(`Columns hidden (${activeHiddenColumnCount})`);
    if (filtersCount > 0) labels.push(`Filters applied (${filtersCount})`);
    return labels;
  }, [activeHiddenColumnCount, filtersCount]);
  const settingLabels = appliedSettingLabels ?? fallbackSettingLabels;

  useEffect(() => {
    if (activeOpen && viewName === undefined) {
      setInternalViewName(defaultViewName);
    }
  }, [activeOpen, defaultViewName, viewName]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && preventClose) return;
    if (open === undefined) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
    if (!nextOpen) onClose?.();
  };

  const updateViewName = (nextViewName: string) => {
    if (viewName === undefined) setInternalViewName(nextViewName);
    onViewNameChange?.(nextViewName);
  };

  const handleCancel = () => {
    onCancel?.();
    handleOpenChange(false);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = activeViewName.trim();
    if (!name || submitDisabled) return;

    const payload: SaveViewPayload = {
      view_type: viewType ?? "orders",
      name,
      columns: (columns ?? []).map((col, index) => ({
        key: col.id,
        order: index,
        visible: visibleColumnIds.includes(col.id),
      })),
      filters: filters ?? [],
      priority,
    };

    const result = onSave?.(payload);

    if (isPromiseLike(result)) {
      setInternalLoading(true);
      void result
        .then((resolved) => {
          if (resolved && typeof resolved === "object" && "view" in resolved) {
            onSaved?.(resolved as SaveViewResult);
          }
        })
        .finally(() => {
          setInternalLoading(false);
          if (closeOnSave) handleOpenChange(false);
        });
      return;
    }

    if (closeOnSave) handleOpenChange(false);
  };

  return (
    <RadixDialog.Root open={activeOpen} onOpenChange={handleOpenChange}>
      {trigger !== null && (
        <RadixDialog.Trigger asChild>
          {trigger ?? (
            <Button size="sm" className={triggerClassName}>
              {triggerLabel}
            </Button>
          )}
        </RadixDialog.Trigger>
      )}
      <RadixDialog.Portal>
        <BfrsPortalScope>
          <SaveViewOverlay />
          <SaveViewContent
            className={className}
            onPointerDownOutside={(event) => {
              if (!closeOnOverlayClick || preventClose) event.preventDefault();
            }}
            onEscapeKeyDown={(event) => {
              if (!closeOnEscape || preventClose) event.preventDefault();
            }}
          >
            <SaveViewForm onSubmit={handleSubmit}>
              <header>
                <SaveViewTitle>{title}</SaveViewTitle>
                {description && (
                  <SaveViewDescription>{description}</SaveViewDescription>
                )}
              </header>

              <SaveViewField>
                <SaveViewLabel htmlFor={inputId}>{viewNameLabel}</SaveViewLabel>
                <SaveViewInput
                  id={inputId}
                  autoFocus
                  value={activeViewName}
                  placeholder={viewNamePlaceholder}
                  disabled={disabled || activeLoading}
                  onChange={(event) => updateViewName(event.target.value)}
                />
              </SaveViewField>

              <SaveViewSection aria-label={typeof appliedLabel === "string" ? appliedLabel : "Applied filters and settings"}>
                <SaveViewSectionTitle>{appliedLabel}</SaveViewSectionTitle>
                <SaveViewChips>
                  {appliedSettings ??
                    (settingLabels.length > 0 ? (
                      settingLabels.map((label) => (
                        <SaveViewChip key={label} ownerState={{}}>
                          {label}
                        </SaveViewChip>
                      ))
                    ) : (
                      <SaveViewChip ownerState={{ muted: true }}>No changes applied</SaveViewChip>
                    ))}
                </SaveViewChips>
              </SaveViewSection>

              <SaveViewFooter>
                <SaveViewCancelButton type="button" variant="secondary" disabled={activeLoading} onClick={handleCancel}>
                  {cancelLabel}
                </SaveViewCancelButton>
                <SaveViewSubmitButton type="submit" loading={activeLoading} disabled={submitDisabled}>
                  {saveLabel}
                </SaveViewSubmitButton>
              </SaveViewFooter>
            </SaveViewForm>
          </SaveViewContent>
        </BfrsPortalScope>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
